import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { useGenerateExport } from 'cli/hooks';
import { ICliJob } from 'cli/types';
import { Steps } from 'core/components';
import { AccessPermissions, ApiStatus, NotificationPosition } from 'core/enums';
import { ICliJobLostPoll, useCliJobHandler } from 'core/hooks';
import {
  FormProvider,
  StepsProvider,
  useFormContext,
  useNotificationContext,
  useStepsContext,
} from 'core/states';
import { IStep } from 'core/types';
import { getLocalDateForServer } from 'core/utils';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useQuoteActions } from 'quotes/hooks';
import { IVendorQuote } from 'quotes/types';
import { Color, Size, TooltipPosition } from 'theme/enums';

import { ExportSkuPartStep } from './enums';
import { mapAttributeFormToSkuExportRequest } from './mappers';
import { SendAsSpaConfirmation } from './SendAsSpaConfirmation';
import { SendAsSpaSuccess } from './SendAsSpaSuccess';
import { Attributes, IAttributesHandle } from './steps/Attributes';
import {
  getPriorityModeFromConfig,
  mapAttributeFormToSpaAudience,
  mapAttributesToForm,
} from './steps/Attributes/mappers';
import { attributeSchema } from './steps/Attributes/schema';
import { Preview } from './steps/Preview';
import {
  IAttributeForm,
  ISendAsSpaConfirmationState,
  ISendAsSpaResult,
  ISkuExportDownloadResult,
  ISkuExportRequest,
} from './types';
import { getSendAsSpaConcurrentSendNotice, getSendAsSpaStatusWriteFailedMessage } from './utils';

// How long the success screen stays up before the drawer auto-closes.
const SEND_AS_SPA_SUCCESS_CLOSE_MS = 2000;

interface IProps {
  quote: IVendorQuote;
  isOpen: boolean;
  onClose: () => void;
}

interface IContentProps {
  quote: IVendorQuote;
  isOpen: boolean;
  onClose: () => void;
}

export const attributesDrawerWidth = 'clamp(560px, 55%, 720px)';
const previewDrawerWidth = 'clamp(720px, 90%, 1440px)';

const StepsWithDynamicWidth = ({ title, isOpen }: { title: string; isOpen: boolean }) => {
  const { activeStep } = useStepsContext();

  const drawerSlotAndSlotProps = useMemo(
    () => ({
      slotProps: {
        content: {
          sx: {
            width:
              activeStep?.type === ExportSkuPartStep.PREVIEW
                ? previewDrawerWidth
                : attributesDrawerWidth,
          },
        },
      },
    }),
    [activeStep?.type]
  );

  return (
    <Steps
      title={title}
      isOpen={isOpen}
      fullWidth={false}
      drawerSlotAndSlotProps={drawerSlotAndSlotProps}
    />
  );
};

const ExportSkuPartContent = ({ quote, isOpen, onClose }: IContentProps) => {
  const { t } = useTranslation();

  const attributesRef = useRef<IAttributesHandle>(null);
  // The steps-context close (passed to the send button's onClick) resets the wizard to step 1 and
  // closes the drawer; stashed here so the deferred success-screen close can call it.
  const closeDrawerRef = useRef<(() => void) | null>(null);
  const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on close: a Download job still polling after it must not notify into the next opening.
  const drawerSessionRef = useRef(0);
  const isSendingRef = useRef(false);

  const [formData, setFormData] = useState<IAttributeForm | null>(null);
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [sendAsSpaSucceeded, setSendAsSpaSucceeded] = useState(false);
  const [wasResend, setWasResend] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // FlintFox holds the file but the stale status still looks sendable: no Send until close.
  const [isStatusWriteFailed, setIsStatusWriteFailed] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState<ISendAsSpaConfirmationState | null>(
    null
  );

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { reset } = useFormContext();
  const { clearNotification, showNotification } = useNotificationContext();
  const { downloadFilePath } = useGenerateExport();
  const { handleCliJob } = useCliJobHandler();
  const { sendVendorQuoteAsSpa, exportQuote } = useQuoteActions();
  const queryClient = useQueryClient();

  const hydratedPriorityMode = useMemo(
    () => getPriorityModeFromConfig(quote?.attributes ?? null),
    [quote?.attributes]
  );

  // Gated by the Send as SPA right on purpose, because this drawer IS the send. The Attributes tab
  // and the import step stay open to every alternate-export user and follow the BE's
  // `canEditAttributes`, so do not merge the two gates. Without the right the drawer opens straight
  // on the Preview, which exports the stored Attributes as they stand.
  const isAttributesStepShown =
    hasPermission(AccessPermissions.SEND_AS_SPA) &&
    VendorQuoteHelper.isAttributesAvailable({ quote, user });

  const storedFormData = useMemo(
    () => mapAttributesToForm(quote?.attributes ?? null),
    [quote?.attributes]
  );

  const canSendAsSpa = useMemo(
    () =>
      VendorQuoteHelper.isSkuExportAvailable({ quote, user }) &&
      hasPermission(AccessPermissions.SEND_AS_SPA),
    [quote, user, hasPermission]
  );

  const previewedFormData = isAttributesStepShown ? formData : storedFormData;

  // Same gate as the VQ-detail SendAsSpaButton (first send / resend / sent and unchanged / in
  // flight) minus the Attributes preflight. Download stays available.
  const sendButtonState = useMemo(
    () =>
      VendorQuoteHelper.getSpaSendButtonState({
        quote,
        spaAudienceRights: VendorQuoteHelper.getSpaAudienceRights(user),
        isSending,
        skipAttributeBlockers: true,
        previewedAudience: previewedFormData
          ? mapAttributeFormToSpaAudience(previewedFormData)
          : null,
      }),
    [quote, user, isSending, previewedFormData]
  );

  let sendButtonTooltip: string | null = null;
  if (sendButtonState.tooltip) {
    sendButtonTooltip = t(sendButtonState.tooltip.key, sendButtonState.tooltip.options);
  } else if (!isPreviewReady) {
    sendButtonTooltip = t('tooltipSendAsSpaPreviewNotReady');
  }

  // Same lock the Attributes tab enforces. The BE skips the export-time attribute persist for a
  // blocked user, so offering the inputs here would silently discard whatever they typed —
  // Preview/Download still run off the stored values.
  const isAttributesReadOnly = VendorQuoteHelper.isAttributesEditBlocked(quote);
  // Same rule as the Preview step: the Selected SPA flag only rides along when it can be saved.
  const canEnableSelectableSpa =
    hasPermission(AccessPermissions.SELECTABLE_SPA) && !isAttributesReadOnly;
  const canChooseSpaStatus = hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA);

  const handleOnClose = useCallback(() => {
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    drawerSessionRef.current += 1;
    clearNotification();
    setFormData(null);
    setIsPreviewReady(false);
    setIsDownloading(false);
    setHasSubmitted(false);
    setSendAsSpaSucceeded(false);
    setIsStatusWriteFailed(false);
    setWasResend(false);
    setSendConfirmation(null);
    reset(mapAttributesToForm(quote?.attributes ?? null));
    onClose();
  }, [clearNotification, onClose, quote?.attributes, reset]);

  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);

  // Clear the deferred success-close timer if the tree unmounts mid-window (e.g. navigation),
  // so it can't fire after unmount. Normal closes clear it in handleOnClose.
  useEffect(
    () => () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    },
    []
  );

  const onPreviewClick = useCallback(async () => {
    setHasSubmitted(true);

    // Read-only: the stored values are exported as they stand. Running the schema would strand the
    // user on errors in fields they cannot edit (e.g. Valid Until moved before the start date).
    const data = isAttributesReadOnly
      ? attributesRef.current?.getFormValues()
      : await attributesRef.current?.triggerFormSubmit();

    if (!data) {
      return false;
    }

    setFormData(data);
    setIsPreviewReady(false);
  }, [isAttributesReadOnly]);

  // The Preview stores nothing, so Download runs the export again, this time stored.
  const onDownloadClick = useCallback(() => {
    const exportFormData = isAttributesStepShown ? formData : storedFormData;

    if (!isPreviewReady || isDownloading || !exportFormData) {
      return false;
    }

    const drawerSession = drawerSessionRef.current;
    const isCurrentDrawer = () => drawerSession === drawerSessionRef.current;

    setIsDownloading(true);

    // handleCliJob swallows an enqueue-time throw without reaching the terminal callbacks.
    const enqueue = async (payload: ISkuExportRequest) => {
      try {
        return await exportQuote(payload);
      } catch (error) {
        if (isCurrentDrawer()) {
          setIsDownloading(false);
        }

        throw error;
      }
    };

    const onDownloadFinished = (cliJob: ICliJob) => {
      const result = cliJob.result as ISkuExportDownloadResult | null;
      const fileId = result?.zipFileId || result?.fileId;

      queryClient.invalidateQueries(['quote']);
      queryClient.invalidateQueries(['quoteFiles']);

      if (fileId) {
        window.location.href = downloadFilePath(String(fileId));
      }

      if (!isCurrentDrawer()) {
        return;
      }

      setIsDownloading(false);

      if (!fileId) {
        showNotification({
          type: ApiStatus.ERROR,
          title: t('titleError'),
          message: t('messageExportSkuPartsFileMissing'),
          position: NotificationPosition.DRAWER,
        });
      } else if (!isSendingRef.current) {
        // Only the download's own progress notice; an in-flight send keeps its notice.
        clearNotification();
      }
    };

    const onDownloadStopped = (cliJob: ICliJob) => {
      if (!isCurrentDrawer()) {
        return;
      }

      setIsDownloading(false);
      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message: cliJob.statusDescription || t('serverError'),
        position: NotificationPosition.DRAWER,
      });
    };

    const onDownloadPollFailed = () => {
      if (!isCurrentDrawer()) {
        return;
      }

      setIsDownloading(false);
      showNotification({
        type: ApiStatus.WARNING,
        title: t('titleWarning'),
        message: t('messageExportLostTrack'),
        position: NotificationPosition.DRAWER,
      });
    };

    handleCliJob(
      enqueue,
      mapAttributeFormToSkuExportRequest({
        quoteId: quote.id,
        formData: exportFormData,
        persist: true,
        canChooseSpaStatus,
        canEnableSelectableSpa,
        localDate: getLocalDateForServer(),
      }),
      {
        cliJobInProgressMessage: t('messageGenerateExportSkuPartsInProgress'),
        notificationPosition: NotificationPosition.DRAWER,
        onCliJobFinished: onDownloadFinished,
        onCliJobStopped: onDownloadStopped,
        onCliJobPollFailed: onDownloadPollFailed,
      }
    );

    // Download is a side effect, not a wizard-completing action. Without the explicit false the
    // Steps final-step fallback closes the drawer ~1s later — which also kills an in-flight
    // Send as SPA (drawer closes mid-send and its success screen never shows).
    return false;
  }, [
    isAttributesStepShown,
    formData,
    storedFormData,
    isPreviewReady,
    isDownloading,
    exportQuote,
    queryClient,
    downloadFilePath,
    showNotification,
    clearNotification,
    t,
    handleCliJob,
    quote.id,
    canChooseSpaStatus,
    canEnableSelectableSpa,
  ]);

  const onBackToAttributes = useCallback(() => {
    setFormData(null);
    setIsPreviewReady(false);
  }, []);

  const onSendAsSpaFinished = useCallback(
    (cliJob: ICliJob) => {
      // The BE writes the terminal trade_agreement_status/error (Sent on success, Failed + error on
      // a rejected upload) before the job reaches "finished", so refetch the quote on BOTH outcomes
      // — otherwise a failed send leaves the detail (Attributes tab) showing the stale value.
      setIsSending(false);
      queryClient.invalidateQueries(['quote']);
      queryClient.invalidateQueries(['quoteFiles']);

      const result = cliJob.result as ISendAsSpaResult | null;

      const statusWriteFailedMessage = getSendAsSpaStatusWriteFailedMessage({
        result,
        isBulk: false,
        t,
      });

      if (statusWriteFailedMessage) {
        setIsStatusWriteFailed(true);
        showNotification({
          type: ApiStatus.ERROR,
          title: t('titleError'),
          message: statusWriteFailedMessage,
          position: NotificationPosition.DRAWER,
        });

        return;
      }

      // The job finishes "finished" even on a rejected upload — `result.sent` is the real signal.
      if (result?.sent !== true) {
        const concurrentSendNotice = getSendAsSpaConcurrentSendNotice(result);

        // Prefer the BE error; else point at the step that failed (mfr_part | trade_agreement).
        let message = t('messageSendAsSpaError');
        if (concurrentSendNotice) {
          message = t(concurrentSendNotice.key);
        } else if (result?.error) {
          message = result.error;
        } else if (result?.failedStep) {
          message = t('messageSendAsSpaErrorAtStep', {
            step: result.failedStep.replaceAll('_', ' '),
          });
        }

        showNotification({
          type: concurrentSendNotice?.isWarning ? ApiStatus.WARNING : ApiStatus.ERROR,
          title: t(concurrentSendNotice?.isWarning ? 'titleWarning' : 'titleError'),
          message,
          position: NotificationPosition.DRAWER,
        });

        return;
      }

      clearNotification();
      setWasResend((result.resendQuoteIds?.length ?? 0) > 0);
      setSendAsSpaSucceeded(true);

      autoCloseTimeoutRef.current = setTimeout(() => {
        closeDrawerRef.current?.();
      }, SEND_AS_SPA_SUCCESS_CLOSE_MS);
    },
    [showNotification, clearNotification, queryClient, t]
  );

  const onSendAsSpaStopped = useCallback(
    (cliJob: ICliJob) => {
      // A stopped/crashed job may still have written a Failed status before dying; refetch so the
      // detail doesn't keep showing the pre-send value.
      setIsSending(false);
      queryClient.invalidateQueries(['quote']);

      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message: cliJob.statusDescription || t('messageSendAsSpaError'),
        position: NotificationPosition.DRAWER,
      });
    },
    [showNotification, queryClient, t]
  );

  // A second send while this one still runs is skipped by the BE per-quote send lock.
  const onSendAsSpaPollFailed = useCallback(
    async ({ showLostTrackNotice }: ICliJobLostPoll) => {
      await Promise.all([
        queryClient.invalidateQueries(['quote']),
        queryClient.invalidateQueries(['quoteFiles']),
      ]);

      setIsSending(false);
      showLostTrackNotice();
    },
    [queryClient]
  );

  const startSendAsSpa = useCallback(
    (localDate: string) => {
      setIsSending(true);

      // handleCliJob swallows an enqueue-time throw without reaching the terminal callbacks.
      const enqueue = async (payload: { quoteId: number; localDate: string }) => {
        try {
          return await sendVendorQuoteAsSpa(payload);
        } catch (error) {
          setIsSending(false);
          throw error;
        }
      };

      handleCliJob(
        enqueue,
        { quoteId: quote.id, localDate },
        {
          cliJobInProgressMessage: t(
            sendButtonState.isResend ? 'messageResendAsSpaInProgress' : 'messageSendAsSpaInProgress'
          ),
          cliJobErrorMessage: t('messageSendAsSpaError'),
          notificationPosition: NotificationPosition.DRAWER,
          onCliJobFinished: onSendAsSpaFinished,
          onCliJobStopped: onSendAsSpaStopped,
          onCliJobPollFailed: onSendAsSpaPollFailed,
          preferBackendError: true,
        }
      );
    },
    [
      handleCliJob,
      sendVendorQuoteAsSpa,
      quote.id,
      t,
      sendButtonState.isResend,
      onSendAsSpaFinished,
      onSendAsSpaStopped,
      onSendAsSpaPollFailed,
    ]
  );

  const onSendAsSpaClick = useCallback(
    (onNextClickSuccess?: () => void) => {
      if (!isPreviewReady) {
        return false;
      }

      closeDrawerRef.current = onNextClickSuccess ?? null;

      // A resend ends the live agreement in Flintfox and a past Valid Until needs the user's word,
      // so either is confirmed first.
      const localDate = getLocalDateForServer();
      const pastValidUntil = VendorQuoteHelper.isValidUntilInPast(quote, localDate)
        ? VendorQuoteHelper.getValidUntilDay(quote)
        : null;

      if (sendButtonState.isResend || pastValidUntil) {
        setSendConfirmation({ localDate, pastValidUntil });
      } else {
        startSendAsSpa(localDate);
      }

      // Prevent the Steps built-in auto-close; the success screen + timed close is driven above.
      return false;
    },
    [isPreviewReady, quote, sendButtonState.isResend, startSendAsSpa]
  );

  const onSendConfirm = useCallback(() => {
    if (!sendConfirmation) {
      return;
    }

    setSendConfirmation(null);
    startSendAsSpa(sendConfirmation.localDate);
  }, [sendConfirmation, startSendAsSpa]);

  const steps = useMemo<IStep[]>(
    () => [
      ...(isAttributesStepShown
        ? [
            {
              type: ExportSkuPartStep.FORM,
              title: t('titleAttributes'),
              content: (
                <Attributes
                  ref={attributesRef}
                  hasSubmitted={hasSubmitted}
                  hydratedPriorityMode={hydratedPriorityMode}
                  validUntil={quote?.validUntil}
                  isReadOnly={isAttributesReadOnly}
                />
              ),
              nextButton: {
                text: t('buttonLabelPreview'),
                options: { onClick: onPreviewClick },
              },
            },
          ]
        : []),
      {
        type: ExportSkuPartStep.PREVIEW,
        title: t('titleExportSkuPartsPreviewStep'),
        content: sendAsSpaSucceeded ? (
          <SendAsSpaSuccess isResend={wasResend} />
        ) : (
          <Preview
            quote={quote}
            formData={isAttributesStepShown ? formData : storedFormData}
            onPreviewReady={setIsPreviewReady}
          />
        ),
        previousButton: {
          options: { onClick: onBackToAttributes, disabled: sendAsSpaSucceeded },
        },
        nextButtons: [
          {
            text: t('buttonLabelDownload'),
            options: {
              onClick: onDownloadClick,
              disabled: !isPreviewReady || isDownloading || sendAsSpaSucceeded,
            },
          },
          ...(canSendAsSpa
            ? [
                {
                  text: t(
                    sendButtonState.isResend ? 'buttonLabelResendAsSpa' : 'buttonLabelSendAsSpa'
                  ),
                  options: {
                    onClick: onSendAsSpaClick,
                    disabled:
                      !isPreviewReady ||
                      sendAsSpaSucceeded ||
                      isStatusWriteFailed ||
                      sendButtonState.isDisabled,
                  },
                  // Not while the success screen is up: the ['quote'] refetch flips the status to
                  // Sent mid-window and the button would advertise "already sent" over it. Button
                  // wraps the Tooltip around a Box that still gets hover while the disabled button
                  // is pointer-transparent, so no pointer-events override is needed.
                  tooltipProps:
                    sendButtonTooltip && !sendAsSpaSucceeded
                      ? {
                          placement: TooltipPosition.TOP,
                          color: Color.NEUTRAL,
                          size: Size.SM,
                          title: sendButtonTooltip,
                          children: null,
                        }
                      : undefined,
                },
              ]
            : []),
        ],
      },
    ],
    [
      t,
      quote,
      formData,
      storedFormData,
      isAttributesStepShown,
      isPreviewReady,
      isDownloading,
      hasSubmitted,
      hydratedPriorityMode,
      sendAsSpaSucceeded,
      isStatusWriteFailed,
      wasResend,
      canSendAsSpa,
      sendButtonState,
      sendButtonTooltip,
      isAttributesReadOnly,
      onPreviewClick,
      onDownloadClick,
      onBackToAttributes,
      onSendAsSpaClick,
    ]
  );

  return (
    <StepsProvider steps={steps} onClose={handleOnClose}>
      <StepsWithDynamicWidth title={t('titleExportSkuParts')} isOpen={isOpen} />

      <SendAsSpaConfirmation
        isOpen={sendConfirmation !== null}
        isResend={sendButtonState.isResend}
        sentVersion={quote?.tradeAgreementSentVersion}
        pastValidUntil={sendConfirmation?.pastValidUntil}
        // What the Preview exported and persisted; the quote refetch may still be in flight.
        spaStatus={(isAttributesStepShown ? formData : storedFormData)?.spaStatus}
        onConfirm={onSendConfirm}
        onCancel={() => setSendConfirmation(null)}
      />
    </StepsProvider>
  );
};

export const ExportSkuPartSteps = ({ quote, isOpen, onClose }: IProps) => {
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  // Saved attributes ride on the quote details record, so prefill is available
  // synchronously with no separate fetch.
  const defaultValues = useMemo(
    () => mapAttributesToForm(quote?.attributes ?? null),
    [quote?.attributes]
  );

  return (
    <FormProvider
      schema={attributeSchema(
        quote?.validUntil,
        VendorQuoteHelper.getSpaAudienceRights(user),
        hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA)
      )}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <ExportSkuPartContent quote={quote} isOpen={isOpen} onClose={onClose} />
    </FormProvider>
  );
};
